import React, { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { appInsightsManager } from '../../../services/appInsights/appInsightsManager';
import { ErrorContainer, ErrorMessage, ErrorTitle } from './styles';
import Logo from '../../../assets/Logo';
import ActionButton from '../ActionButton/ActionButton';
import { useI18n } from '../../../hooks/useI18n';
import { TranslationKeys } from '../../../i18n/locales/defaultResources';
import TranslationModal from '../../features/translation/TranslationModal/TranslationModal';

const useErrorBoundary = () => {
  const [hasError, setHasError] = useState(false);

  const logError = (error: ErrorEvent) => {
    console.error('Error occurred:', error);
    appInsightsManager.trackError(error.error);
  };

  const catchError = (error: ErrorEvent) => {
    setHasError(true);
    logError(error);
  };

  return { hasError, catchError };
};

const ErrorBoundary: React.FC<{ children?: ReactNode | undefined }> = ({
  children,
}) => {
  const { hasError, catchError } = useErrorBoundary();
  const navigate = useNavigate();
  const { t } = useI18n();
  useEffect(() => {
    const errorHandler = (error: ErrorEvent) => {
      catchError(error);
    };

    window.addEventListener('error', errorHandler);
    return () => {
      window.removeEventListener('error', errorHandler);
    };
  }, [catchError]);

  const handleRedirectHome = () => {
    navigate('/');
    window.location.reload();
  };

  if (hasError) {
    return (
      <ErrorContainer>
        <Logo />
        <TranslationModal />
        <ErrorTitle>{t(TranslationKeys.error_boundary_title)}</ErrorTitle>
        <ErrorMessage>{t(TranslationKeys.error_boundary_message)}</ErrorMessage>
        <ActionButton
          onClickAction={handleRedirectHome}
          buttonText={t(TranslationKeys.error_boundary_cta)}
          variant="link-button"
        />
      </ErrorContainer>
    );
  }

  return <>{children}</>;
};

export default ErrorBoundary;
